import type { QueryClient } from '@tanstack/react-query';
import type { Candidate, CandidateRecommendation } from '../../types';
import {
  getApplicationDetail,
  getPipelineBoard,
  type ApiApplicationCard,
  type ApiApplicationDetail,
  type ApiApplicationStage,
  type ApiApplicationNote,
  type PipelineBoardQuery,
} from '../../libs/api/pipelineService';

export const CANDIDATES_QUERY_KEY = ['candidates'] as const;

const mapApiStageToUi = (stage?: string | null): Candidate['stage'] => {
  if (stage === 'SCREENING') return 'Screening';
  if (stage === 'INTERVIEW') return 'Interview';
  if (stage === 'OFFER') return 'Offer';
  if (stage === 'HIRED') return 'Hired';
  if (stage === 'REJECTED') return 'Rejected';
  return 'New';
};

const mapScoreToRecommendation = (score?: number | null): CandidateRecommendation | undefined => {
  if (score === null || score === undefined) return undefined;
  if (score >= 85) return 'Strongly Recommend';
  if (score >= 70) return 'Consider';
  return 'Low Fit';
};

export const mapCardToCandidate = (card: ApiApplicationCard): Candidate => ({
  id: card.candidate.id,
  resumeId: card.resumesId,
  applicationId: card.id,
  name: card.candidate.full_name || 'Unknown Candidate',
  email: card.candidate.primary_email || '',
  phone: card.candidate.primary_phone || '',
  dateOfBirth: card.candidate.date_of_birth ?? null,
  role: card.job_description.title,
  stage: mapApiStageToUi(card.current_stage),
  status: card.current_stage === 'REJECTED' ? 'Inactive' : 'Active',
  appliedDate: new Date(card.applied_at).toISOString().split('T')[0] || '',
  jdId: card.job_description.id,
  department: card.job_description.department,
  source: card.source ?? null,
  channel: card.channel ?? null,
  nextInterviewAt: card.next_interview_at ?? null,
  nextInterviewDurationMinutes: card.next_interview_duration_minutes ?? null,
  nextInterviewResult: card.next_interview_result ?? null,
  nextInterviewRound: card.next_interview_round ?? null,
  score: card.ai_match_score ?? undefined,
  yearsOfExperience: card.years_of_experience ?? undefined,
  recommendation: mapScoreToRecommendation(card.ai_match_score),
  kanbanNote: card.latest_note_preview || '',
});

export const getLatestApplicationNoteText = (notes?: ApiApplicationNote[]): string => {
  if (!notes?.length) {
    return '';
  }

  const latestNote = notes.reduce<ApiApplicationNote | null>((latest, current) => {
    if (!latest) {
      return current;
    }

    const latestTime = Date.parse(latest.updated_at || latest.created_at || '');
    const currentTime = Date.parse(current.updated_at || current.created_at || '');

    if (Number.isNaN(latestTime) && Number.isNaN(currentTime)) {
      return current;
    }

    if (Number.isNaN(latestTime)) {
      return current;
    }

    if (Number.isNaN(currentTime)) {
      return latest;
    }

    return currentTime >= latestTime ? current : latest;
  }, null);

  return latestNote?.note_text || '';
};

export const mapApplicationDetailToCandidate = (detail: ApiApplicationDetail): Candidate => {
  const latestNote = getLatestApplicationNoteText(detail.notes);

  return {
    id: detail.candidate.id,
    resumeId: detail.resume.id,
    resumeOriginalFileName: detail.resume.original_file_name,
    applicationId: detail.id,
    name: detail.candidate.full_name || 'Unknown Candidate',
    email: detail.candidate.primary_email || '',
    phone: detail.candidate.primary_phone || '',
    dateOfBirth: detail.candidate.date_of_birth ?? null,
    role: detail.job_description.title,
    stage: mapApiStageToUi(detail.current_stage),
    status: detail.current_stage === 'REJECTED' ? 'Inactive' : 'Active',
    appliedDate: new Date(detail.applied_at).toISOString().split('T')[0] || '',
    jdId: detail.job_description.id,
    department: detail.job_description.department,
    source: detail.resume.source ?? null,
    channel: detail.resume.channel ?? null,
    score: detail.ai_match_score ?? undefined,
    jdThreshold: detail.job_description.ai_threshold ?? undefined,
    summary: detail.ai_summary ?? undefined,
    yearsOfExperience: detail.years_of_experience ?? undefined,
    skills: (detail.skills || []).filter((item) => Boolean(item.skill)),
    pros: detail.ai_strengths || [],
    cons: detail.ai_gaps || [],
    recommendation: mapScoreToRecommendation(detail.ai_match_score),
    applicationHistory: (detail.application_history || []).map((item) => ({
      id: item.application_id,
      jdId: item.jd.id,
      jdName: item.jd.title,
      resumeId: item.resume_id,
      resumeOriginalFileName: item.original_file_name,
      applyTime: item.applied_at,
      matchingScore: item.matching_score ?? 0,
      finalStage: mapApiStageToUi(item.final_stage),
      note: item.note ?? '',
    })),
    kanbanNote: latestNote,
  };
};

export const syncCandidateNoteCaches = (
  queryClient: QueryClient,
  identifiers: {
    applicationId: string;
    candidateId?: string;
  },
  noteText: string,
) => {
  const matchesProfileCandidate = (candidate: Candidate) =>
    candidate.applicationId === identifiers.applicationId ||
    candidate.id === identifiers.applicationId ||
    (identifiers.candidateId ? candidate.id === identifiers.candidateId : false);

  const matchesListCandidate = (candidate: Candidate) =>
    candidate.applicationId === identifiers.applicationId ||
    candidate.id === identifiers.applicationId;

  const updateProfileCandidate = (candidate: Candidate | null | undefined) => {
    if (!candidate || !matchesProfileCandidate(candidate)) {
      return candidate;
    }

    return {
      ...candidate,
      kanbanNote: noteText,
    };
  };

  const updateListCandidate = (candidate: Candidate) => {
    if (!matchesListCandidate(candidate)) {
      return candidate;
    }

    return {
      ...candidate,
      kanbanNote: noteText,
    };
  };

  queryClient.setQueryData<Candidate | null>(
    ['candidate-profile', identifiers.applicationId],
    updateProfileCandidate,
  );

  if (identifiers.candidateId && identifiers.candidateId !== identifiers.applicationId) {
    queryClient.setQueryData<Candidate | null>(
      ['candidate-profile', identifiers.candidateId],
      updateProfileCandidate,
    );
  }

  queryClient.setQueriesData<Candidate[]>({ queryKey: CANDIDATES_QUERY_KEY }, (previous) =>
    previous?.map(updateListCandidate),
  );
};

export const syncCandidateStageCaches = (
  queryClient: QueryClient,
  identifiers: {
    applicationId: string;
    candidateId?: string;
  },
  nextStage: ApiApplicationStage | null | undefined,
) => {
  const uiStage = mapApiStageToUi(nextStage);
  const nextStatus: Candidate['status'] = nextStage === 'REJECTED' ? 'Inactive' : 'Active';

  const matchesProfileCandidate = (candidate: Candidate) =>
    candidate.applicationId === identifiers.applicationId ||
    candidate.id === identifiers.applicationId ||
    (identifiers.candidateId ? candidate.id === identifiers.candidateId : false);

  const matchesListCandidate = (candidate: Candidate) =>
    candidate.applicationId === identifiers.applicationId ||
    candidate.id === identifiers.applicationId;

  const updateProfileCandidate = (candidate: Candidate | null | undefined) => {
    if (!candidate || !matchesProfileCandidate(candidate)) {
      return candidate;
    }

    return {
      ...candidate,
      stage: uiStage,
      status: nextStatus,
    };
  };

  const updateListCandidate = (candidate: Candidate) => {
    if (!matchesListCandidate(candidate)) {
      return candidate;
    }

    return {
      ...candidate,
      stage: uiStage,
      status: nextStatus,
    };
  };

  queryClient.setQueryData<Candidate | null>(
    ['candidate-profile', identifiers.applicationId],
    updateProfileCandidate,
  );

  if (identifiers.candidateId && identifiers.candidateId !== identifiers.applicationId) {
    queryClient.setQueryData<Candidate | null>(
      ['candidate-profile', identifiers.candidateId],
      updateProfileCandidate,
    );
  }

  queryClient.setQueriesData<Candidate[]>({ queryKey: CANDIDATES_QUERY_KEY }, (previous) =>
    previous?.map(updateListCandidate),
  );
};

export const syncCandidateInterviewCaches = (
  queryClient: QueryClient,
  identifiers: {
    applicationId: string;
    candidateId?: string;
  },
  interview: {
    durationMinutes: number | null;
    result: Candidate['nextInterviewResult'];
    round: string | null;
    startAt: string | null;
  },
) => {
  const matchesCandidate = (candidate: Candidate) =>
    candidate.applicationId === identifiers.applicationId ||
    candidate.id === identifiers.applicationId ||
    (identifiers.candidateId ? candidate.id === identifiers.candidateId : false);

  const updateCandidate = (candidate: Candidate | null | undefined) => {
    if (!candidate || !matchesCandidate(candidate)) {
      return candidate;
    }

    return {
      ...candidate,
      nextInterviewAt: interview.startAt,
      nextInterviewDurationMinutes: interview.durationMinutes,
      nextInterviewResult: interview.result,
      nextInterviewRound: interview.round,
    };
  };

  queryClient.setQueryData<Candidate | null>(
    ['candidate-profile', identifiers.applicationId],
    updateCandidate,
  );

  if (identifiers.candidateId && identifiers.candidateId !== identifiers.applicationId) {
    queryClient.setQueryData<Candidate | null>(
      ['candidate-profile', identifiers.candidateId],
      updateCandidate,
    );
  }

  queryClient.setQueriesData<Candidate[]>({ queryKey: CANDIDATES_QUERY_KEY }, (previous) =>
    previous?.map((candidate) => updateCandidate(candidate) ?? candidate),
  );
};

export const fetchCandidatesFromPipeline = async (
  params?: PipelineBoardQuery,
): Promise<Candidate[]> => {
  const board = await getPipelineBoard(params);
  return (board.columns || [])
    .flatMap((column) => column.items || [])
    .map(mapCardToCandidate)
    .sort((a, b) => {
      const aTime = new Date(a.appliedDate).getTime();
      const bTime = new Date(b.appliedDate).getTime();
      return bTime - aTime;
    });
};

export const findCandidateInPipeline = async (candidateId: string): Promise<Candidate | null> => {
  const list = await fetchCandidatesFromPipeline();
  return list.find((item) => item.id === candidateId || item.applicationId === candidateId) || null;
};

export const fetchCandidateDetail = async (applicationId: string): Promise<Candidate> => {
  const detail = await getApplicationDetail(applicationId);
  return mapApplicationDetailToCandidate(detail);
};
